import React, { useEffect, useState } from 'react';
import SearchableDropdown from '../../FlowBuilder/Common/SearchableDropdown/SearchableDropdown.tsx';
import { useSnackBar } from '../../FlowBuilder/Common/SnackBar/index.tsx';
import { useRecoilState, useRecoilValueLoadable } from 'recoil';
import { generateFilterKey } from '../../../utils/helper.jsx';
import useDebounce from '../../Shared/Delay/index.tsx';
import { campaignDropdownStateAtom } from '../../../Services/_states/index.js';
import { fetchCampaignDropdownListSelector } from '../../../Services/_states/CampaignPlanning/campaignSelector.ts';

interface CampaignDropdownProps {
  value: any;
  onChange: (value: any) => void;
  organisationId: string;
  error?: boolean;
  errorMessage?: string;
  label?: string;
  disabled?: boolean;
  sx?: any;
}

const CampaignDropdown: React.FC<CampaignDropdownProps> = ({
  value,
  onChange,
  error = false,
  errorMessage = '',
  label = 'Select Campaign',
  disabled = false,
  sx = { mb: 1, mt: 1 },
  organisationId,
}) => {
  const { showSnackBar } = useSnackBar();
  const [tableState, setTableState] = useRecoilState(campaignDropdownStateAtom);
  const fetchedData = useRecoilValueLoadable(fetchCampaignDropdownListSelector);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  // Save fresh API response to cache
  useEffect(() => {
    if (fetchedData.state === 'hasValue' && !fetchedData.contents.fromCache) {
      const filterKey = generateFilterKey(tableState.filters);
      setTableState((prev) => ({
        ...prev,
        cache: {
          ...prev.cache,
          [filterKey]: [...(prev.cache[filterKey] || []), ...fetchedData.contents.data],
        },
        totalCountCache: {
          ...prev.totalCountCache,
          [filterKey]: fetchedData.contents.totalCount,
        },
      }));
    }
  }, [fetchedData, setTableState, tableState.filters]);

  // On debounced search query update
  useEffect(() => {
    if (!organisationId) return;

    setTableState((prev) => ({
      ...prev,
      filters: {
        ...prev.filters,
        organisation_id: organisationId,
        search: debouncedSearchQuery,
        next_page: 0,
      },
      cache: {},
      totalCountCache: {},
    }));
  }, [organisationId, debouncedSearchQuery]);

  // Load more campaigns (pagination)
  const loadMoreCampaign = async () => {
    const currentSearch = tableState.filters.search || '';
    const totalCount = tableState.totalCountCache[generateFilterKey(tableState.filters)] || 0;

    const allCachedValues = Object.entries(tableState.cache)
      .filter(([key]) => key.includes(`search=${currentSearch}`))
      .flatMap(([_, val]: any) => val);

    if (allCachedValues.length < totalCount) {
      setTableState((prev) => ({
        ...prev,
        filters: {
          ...prev.filters,
          next_page: prev.filters.next_page + 1,
        },
      }));
    }
  };

  const handleSearchCampaign = (query: string) => {
    setSearchQuery(query);
  };
 
  const currentSearch = tableState.filters.search || '';
  const campaignOptions = Object.entries(tableState.cache)
    .filter(([key]) => key.includes(`search=${currentSearch}`))
    .flatMap(([_, val]: any) => val);

  return (
    <SearchableDropdown
      options={campaignOptions}
      label={label}
      value={value}
      onChange={onChange}
      fetchMoreOptions={loadMoreCampaign}
      onSearch={handleSearchCampaign}
      searchValue={searchQuery}
      labelKey="name"
      valueKey="proposal_id"
      loading={fetchedData.state === 'loading'}
      error={error}
      errorMessage={errorMessage}
      disabled={disabled}
      sx={sx}
    />
  );
};

export default CampaignDropdown;
